import React, { useState, useEffect, useRef } from 'react';
import {
  Brain, MessageSquare, Zap, Target, TrendingUp, AlertTriangle,
  CheckCircle, X, Send, RefreshCw, Sparkles
} from 'lucide-react';
import {
  safeAsync, Badge, emptyMsg, btnPrimary, btnSm, btnDanger,
  cardWrap, neuShadow, neuShadowSm, neuShadowInset, inp
} from './crmShared';

export default function AIAssistantTab({ contacts, canCreate, canUpdate }) {
  const [tab, setTab] = useState('chat'); // 'chat', 'suggestions', 'insights'
  const [context, setContext] = useState('general');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [insights, setInsights] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const messagesEnd = useRef(null);

  const scrollToBottom = () => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = safeAsync(async (text) => {
    if (!text.trim()) return;
    const userMsg = { role: 'user', text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const response = await window.zignagenixAPI.sendCRMAIChat({
        context,
        messages: [...messages, userMsg].map((m) => ({ role: m.role, text: m.text }))
      });
      const aiMsg = { role: 'ai', text: response.text, timestamp: new Date() };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (err) {
      console.error('Chat error:', err);
      alert('Failed to send message');
    } finally {
      setLoading(false);
    }
  });

  const quickPrompts = [
    'Analyze my pipeline',
    'Suggest follow-ups for stale deals',
    'Draft patient outreach email',
    'Revenue forecast',
    'Identify at-risk deals',
    'Treatment plan recommendations'
  ];

  const generateSuggestions = safeAsync(async () => {
    setLoading(true);
    try {
      // Try to get contact or opportunity IDs for context
      const contactId = contacts?.[0]?.id;
      const response = await window.zignagenixAPI.generateCRMAISuggestion({
        contactId,
        opportunityId: undefined
      });
      const newSuggestion = {
        id: Date.now(),
        ...response,
        status: 'pending',
        createdAt: new Date()
      };
      setSuggestions((prev) => [newSuggestion, ...prev]);
    } catch (err) {
      console.error('Generate suggestions error:', err);
      alert('Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  });

  const loadSuggestions = safeAsync(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterType !== 'all') filters.type = filterType;
      if (filterPriority !== 'all') filters.priority = filterPriority;
      if (filterStatus !== 'all') filters.status = filterStatus;
      const result = await window.zignagenixAPI.getCRMAISuggestions(filters);
      // Defensive: coerce truthy non-arrays to [] so later .filter() / .map()
      // calls don't crash with "x.filter is not a function".
      setSuggestions(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Load suggestions error:', err);
      alert('Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  });

  const acceptSuggestion = safeAsync(async (id) => {
    try {
      await window.zignagenixAPI.acceptCRMAISuggestion(id);
      setSuggestions((prev) => prev.map((s) => (s.id === id ? { ...s, status: 'accepted' } : s)));
    } catch (err) {
      console.error('Accept error:', err);
      alert('Failed to accept suggestion');
    }
  });

  const dismissSuggestion = safeAsync(async (id) => {
    try {
      await window.zignagenixAPI.dismissCRMAISuggestion(id);
      setSuggestions((prev) => prev.map((s) => (s.id === id ? { ...s, status: 'dismissed' } : s)));
    } catch (err) {
      console.error('Dismiss error:', err);
      alert('Failed to dismiss suggestion');
    }
  });

  const loadInsights = safeAsync(async () => {
    setLoading(true);
    try {
      const response = await window.zignagenixAPI.sendCRMAIChat({
        context: 'analytics',
        messages: [{ role: 'user', text: 'Generate comprehensive business insights' }]
      });
      setInsights(response);
    } catch (err) {
      console.error('Load insights error:', err);
      alert('Failed to load insights');
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (tab === 'suggestions') loadSuggestions();
    if (tab === 'insights') loadInsights();
  }, [tab, filterType, filterPriority, filterStatus]);

  const typeColors = {
    next_action: '#2E8B98',
    follow_up: '#256F7A',
    deal_insight: '#62BB46',
    risk_alert: '#DC3545',
    upsell: '#F26522',
    task: '#4FA038'
  };

  const priorityColors = {
    low: '#6C757D',
    normal: '#2E8B98',
    high: '#F26522',
    urgent: '#DC3545'
  };

  // ─── AI Chat Sub-Tab ───────────────────────────────────────────────────
  const ChatTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Context Selector */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Context:</label>
        <select
          value={context}
          onChange={(e) => setContext(e.target.value)}
          style={{
            ...inp,
            flex: 1,
            maxWidth: 280
          }}
        >
          <option value="general">General</option>
          <option value="sales_strategy">Sales Strategy</option>
          <option value="patient_outreach">Patient Outreach</option>
          <option value="pipeline_analysis">Pipeline Analysis</option>
          <option value="marketing">Marketing</option>
          <option value="compliance">Compliance</option>
        </select>
      </div>

      {/* Messages Container */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          marginBottom: 16,
          padding: 16,
          background: '#fff',
          borderRadius: 12,
          boxShadow: neuShadowInset,
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}
      >
        {messages.length === 0 ? (
          <div style={{ ...emptyMsg, justifyContent: 'center', display: 'flex', flexDirection: 'column' }}>
            <Brain size={24} color="#2E8B98" style={{ marginBottom: 8 }} />
            Start a conversation with the AI Assistant
          </div>
        ) : (
          messages.map((msg, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 8
              }}
            >
              <div
                style={{
                  maxWidth: '75%',
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: msg.role === 'user' ? '#2E8B98' : '#F8F9FA',
                  color: msg.role === 'user' ? '#fff' : '#212529',
                  fontSize: 13,
                  lineHeight: 1.5,
                  boxShadow: neuShadowSm,
                  display: 'flex',
                  gap: 8,
                  alignItems: msg.role === 'ai' ? 'flex-start' : 'auto'
                }}
              >
                {msg.role === 'ai' && <Brain size={14} style={{ marginTop: 2, flexShrink: 0 }} />}
                <span>{msg.text}</span>
              </div>
            </div>
          ))
        )}
        {loading && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Brain size={14} color="#2E8B98" />
            <span style={{ fontSize: 13, color: '#6C757D', fontStyle: 'italic' }}>AI is thinking...</span>
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      {/* Quick Prompts */}
      {messages.length === 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 11, color: '#6C757D', fontWeight: 600, marginBottom: 8 }}>Quick Prompts:</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
            {quickPrompts.map((prompt, i) => (
              <button
                key={i}
                onClick={() => sendMessage(prompt)}
                disabled={loading}
                style={{
                  ...btnSm,
                  textAlign: 'left',
                  whiteSpace: 'pre-wrap',
                  height: 'auto',
                  minHeight: 36,
                  opacity: loading ? 0.6 : 1
                }}
              >
                <Sparkles size={12} /> {prompt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Bar */}
      <div style={{ display: 'flex', gap: 8 }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage(input);
            }
          }}
          placeholder="Ask the AI Assistant..."
          style={{
            ...inp,
            flex: 1,
            minHeight: 80,
            maxHeight: 120,
            resize: 'vertical',
            fontFamily: 'inherit'
          }}
          disabled={loading}
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={loading || !input.trim()}
          style={{
            ...btnPrimary,
            alignSelf: 'flex-end',
            padding: '12px 16px',
            opacity: loading || !input.trim() ? 0.6 : 1
          }}
        >
          <Send size={14} /> Send
        </button>
      </div>
    </div>
  );

  // ─── Suggestions Sub-Tab ─────────────────────────────────────────────
  const SuggestionsTab = () => {
    const filtered = suggestions.filter((s) => {
      if (filterType !== 'all' && s.type !== filterType) return false;
      if (filterPriority !== 'all' && s.priority !== filterPriority) return false;
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      return true;
    });

    return (
      <div>
        {/* Header & Filters */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={generateSuggestions}
            disabled={loading}
            style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }}
          >
            <Zap size={14} /> Generate New Suggestions
          </button>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ ...inp, flex: 1, minWidth: 120, maxWidth: 150 }}
          >
            <option value="all">All Types</option>
            <option value="next_action">Next Action</option>
            <option value="follow_up">Follow-up</option>
            <option value="deal_insight">Deal Insight</option>
            <option value="risk_alert">Risk Alert</option>
            <option value="upsell">Upsell</option>
            <option value="task">Task</option>
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            style={{ ...inp, flex: 1, minWidth: 120, maxWidth: 150 }}
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{ ...inp, flex: 1, minWidth: 120, maxWidth: 150 }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>

        {/* Suggestions List */}
        {filtered.length === 0 ? (
          <div style={cardWrap}>
            <div style={emptyMsg}>No suggestions match the current filters.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {filtered.map((sugg) => (
              <div
                key={sugg.id}
                style={{
                  ...cardWrap,
                  padding: 16,
                  border: `1px solid ${typeColors[sugg.type]}22`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                      <Badge text={sugg.type} color={typeColors[sugg.type]} />
                      <Badge text={sugg.priority} color={priorityColors[sugg.priority]} />
                      {sugg.status === 'accepted' && <Badge text="Accepted" color="#4FA038" />}
                      {sugg.status === 'dismissed' && <Badge text="Dismissed" color="#6C757D" />}
                    </div>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: '#212529', margin: '0 0 6px 0' }}>{sugg.title}</h4>
                    <p style={{ fontSize: 12, color: '#6C757D', margin: '0 0 8px 0', lineHeight: 1.4 }}>{sugg.description}</p>
                    {sugg.contactName && (
                      <p style={{ fontSize: 11, color: '#2E8B98', fontWeight: 600, margin: 0 }}>Contact: {sugg.contactName}</p>
                    )}
                  </div>
                </div>

                {/* Confidence Score */}
                {sugg.confidence && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: '#6C757D', marginBottom: 4 }}>Confidence: {Math.round(sugg.confidence * 100)}%</div>
                    <div
                      style={{
                        height: 4,
                        background: '#E8EEF5',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${sugg.confidence * 100}%`,
                          background: `linear-gradient(90deg, #2E8B98, #62BB46)`,
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  {sugg.status === 'pending' && (
                    <>
                      <button
                        onClick={() => acceptSuggestion(sugg.id)}
                        style={{
                          ...btnSm,
                          background: '#62BB46',
                          color: '#fff',
                          flex: 1
                        }}
                      >
                        <CheckCircle size={12} /> Accept
                      </button>
                      <button
                        onClick={() => dismissSuggestion(sugg.id)}
                        style={{
                          ...btnDanger,
                          flex: 1
                        }}
                      >
                        <X size={12} /> Dismiss
                      </button>
                    </>
                  )}
                  {sugg.status !== 'pending' && (
                    <div style={{ fontSize: 12, color: '#6C757D', flex: 1 }}>
                      {sugg.status === 'accepted' ? 'Accepted on ' : 'Dismissed on '} {sugg.updatedAt ? new Date(sugg.updatedAt).toLocaleDateString() : 'N/A'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ─── Insights Sub-Tab ──────────────────────────────────────────────────
  const InsightsTab = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button
          onClick={loadInsights}
          disabled={loading}
          style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }}
        >
          <RefreshCw size={14} /> Refresh Insights
        </button>
      </div>

      {!insights ? (
        <div style={cardWrap}>
          <div style={emptyMsg}>Click Refresh Insights to load data.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {[
            { title: 'Pipeline Health', icon: Target, section: 'pipeline' },
            { title: 'Revenue Trends', icon: TrendingUp, section: 'revenue' },
            { title: 'Team Performance', icon: Zap, section: 'team' },
            { title: 'Risk Alerts', icon: AlertTriangle, section: 'risks' }
          ].map((item, i) => {
            const Icon = item.icon;
            const content = insights[item.section] || insights.text || 'No data available';
            return (
              <div key={i} style={cardWrap}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    paddingBottom: 12,
                    borderBottom: '1px solid #E8EEF5'
                  }}
                >
                  <Icon size={16} color="#2E8B98" />
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: '#212529', margin: 0 }}>{item.title}</h3>
                </div>
                <div
                  style={{
                    padding: '12px 0',
                    fontSize: 12,
                    color: '#6C757D',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Main Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Brain size={18} color="#2E8B98" />
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>AI Assistant Hub</h3>
      </div>

      {/* Sub-Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #E8EEF5', paddingBottom: 12 }}>
        {[
          { id: 'chat', label: 'AI Chat', icon: MessageSquare },
          { id: 'suggestions', label: 'Suggestions', icon: Zap },
          { id: 'insights', label: 'Insights', icon: TrendingUp }
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 12px',
              border: 'none',
              background: tab === t.id ? '#2E8B98' : '#F8F9FA',
              color: tab === t.id ? '#fff' : '#6C757D',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.2s ease',
              boxShadow: tab === t.id ? neuShadowSm : 'none'
            }}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ minHeight: 400 }}>
        {tab === 'chat' && <ChatTab />}
        {tab === 'suggestions' && <SuggestionsTab />}
        {tab === 'insights' && <InsightsTab />}
      </div>
    </div>
  );
}
